import { AuthResult } from "../enums/authResult";
import type { AxiosRequestConfig } from "axios";
import { API_BASEURL } from "./baseURL";

type TokenClient = {
  initialize: () => Promise<void>;
  acquireTokenSilent: (request: {
    scopes: string[];
    account: any;
    forceRefresh?: boolean;
  }) => Promise<{ accessToken: string }>;
};

export const defaultHeader = {
  "Access-Control-Allow-Credentials": "true",
  "Access-Control-Allow-Headers": "*",
  "Access-Control-Allow-Origin": "*",
};

export const authHeader = () => {
  const accessToken = localStorage.getItem(AuthResult.AccessToken);
  return {
    "Access-Control-Allow-Credentials": "true",
    "Access-Control-Allow-Headers": "*",
    "Access-Control-Allow-Origin": "*",
    ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
  };
};

export const cognitoHeader = {
  "Access-Control-Allow-Credentials": "true",
  "Access-Control-Allow-Headers": "*",
  "Access-Control-Allow-Origin": "*",
  "X-Amz-Target": "AWSCognitoIdentityProviderService.InitiateAuth",
  "Content-Length": "134",
  "Content-Type": "application/x-amz-json-1.1",
};

export const normalizeAxiosError = (error: Error & { response?: unknown }) => {
  if (error.response) {
    return error;
  }

  return Object.assign(error, { response: { status: 0 } });
};

export const getApiAccessToken = async (
  instance: TokenClient,
  account: any,
  forceRefresh = false,
) => {
  const apiScope = process.env.REACT_APP_API_SCOPE;
  if (!apiScope) {
    throw new Error("API access scope is not configured.");
  }

  await instance.initialize();
  const request = {
    scopes: [apiScope],
    account,
    ...(forceRefresh ? { forceRefresh } : {}),
  };
  const tokenResponse = await instance.acquireTokenSilent(request);
  localStorage.setItem(AuthResult.AccessToken, tokenResponse.accessToken);
  return tokenResponse.accessToken;
};

export const authorizeApiRequest = async <T extends AxiosRequestConfig>(
  config: T,
  instance: TokenClient & {
    getActiveAccount: () => any;
    getAllAccounts: () => any[];
  },
) => {
  if (!config.url?.startsWith(API_BASEURL)) {
    return config;
  }

  const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
  if (!account) {
    throw new Error("No account is available for API access token acquisition.");
  }

  const token = await getApiAccessToken(instance, account);
  config.headers = {
    ...config.headers,
    Authorization: `Bearer ${token}`,
  } as T["headers"];
  return config;
};
